import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { Adjustment, AdjustmentUnit } from '@oolio-group/domain';
import { useCurrency, useTranslation } from '@oolio-group/localization';
import { getDisplayAdjustmentValue } from '@oolio-group/order-helper';
import { getStyles } from './CartTotals.styles';
import { useTheme } from '../../../hooks/useTheme';

interface Props {
  amountDue: number;
  subTotal: number;
  adjustments: Adjustment[];
  totalTax?: number;
  discounts?: Adjustment[];
  surcharges?: Adjustment[];
  paymentSurcharge?: number;
}

const formatAdjustmentLabel = (
  adjustment: Adjustment,
  fallbackName: string,
  formatCurrency: (amount: number) => string,
): string => {
  const name =
    adjustment.name && adjustment.name !== '' ? adjustment.name : fallbackName;
  const value =
    adjustment.adjustmentUnit === AdjustmentUnit.PERCENTAGE
      ? `${adjustment.amount}%`
      : formatCurrency(adjustment.amount);
  return `${name} (${value})`;
};

interface RowProps {
  label: string;
  value: string;
  testID?: string;
  styles: ReturnType<typeof getStyles>;
}

const MinorRow: React.FC<RowProps> = ({ label, value, testID, styles }) => (
  <View style={styles.row}>
    <Text style={styles.labelMinor} numberOfLines={1}>
      {label}
    </Text>
    <Text style={styles.valueMinor} testID={testID}>
      {value}
    </Text>
  </View>
);

export const Totals: React.FC<Props> = ({
  amountDue,
  subTotal,
  adjustments,
  totalTax = 0,
  discounts = [],
  surcharges = [],
  paymentSurcharge = 0,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => getStyles(colors), [colors]);

  const { translate } = useTranslation();
  const { formatCurrency } = useCurrency();

  return (
    <View style={styles.container}>
      <View style={styles.row}>
        <Text style={styles.label}>
          {translate('cdsPaymentScreen.amountDue')}
        </Text>
        <Text style={styles.value}>{formatCurrency(amountDue)}</Text>
      </View>

      {totalTax > 0 && (
        <MinorRow
          label={translate('payment.totalTaxes')}
          value={formatCurrency(totalTax)}
          testID="order-taxes"
          styles={styles}
        />
      )}

      {discounts.map((discount, index) => (
        <MinorRow
          key={`${discount.id}-${index}`}
          label={formatAdjustmentLabel(discount, 'Discount', formatCurrency)}
          value={formatCurrency(
            getDisplayAdjustmentValue(subTotal, adjustments, discount),
          )}
          testID="order-discount"
          styles={styles}
        />
      ))}

      {surcharges.map(surcharge => (
        <MinorRow
          key={surcharge.id}
          label={formatAdjustmentLabel(surcharge, 'Surcharge', formatCurrency)}
          value={formatCurrency(
            getDisplayAdjustmentValue(subTotal, adjustments, surcharge),
          )}
          testID="order-surcharge"
          styles={styles}
        />
      ))}

      {paymentSurcharge > 0 && (
        <MinorRow
          label={translate('shift.totalPaymentSurcharge')}
          value={formatCurrency(paymentSurcharge)}
          testID="order-paymentValue"
          styles={styles}
        />
      )}
    </View>
  );
};
